import React from 'react';
import { Router } from './Routes/Routes';
import './containers/Home/Home.css';
import AppProvider from './containers/AppProvider/AppProvider';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { queryClient } from './react-query/useQueryClientGet';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './index.css';
import { RouterProvider } from 'react-router';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './utils/AppInsights/AppInsightsService';
import { Color, createTheme, ThemeProvider } from '@mui/material';
import { grey } from '@mui/material/colors';
import MaintenanceContainer from './containers/Maintenance/MaintenanceContainer';

declare module '@mui/material/styles' {
  interface Theme {
    lightGrey: string;
    color: {
      navy: string;
      lightGreen: Color;
      orange: Color;
    };
  }
  // allow configuration using `createTheme()`
  interface ThemeOptions {
    lightGrey?: string;
    color?: {
      navy?: string;
      lightGreen?: Color;
      orange?: Color;
    };
  }
}

function App() {
  const theme = createTheme({
    color: {
      navy: '#006487',
      lightGreen: {
        50: '#fafde8',
        100: '#f3f9ce',
        200: '#e6f3a3',
        300: '#d3e96d',
        400: '#bddb40',
        500: '#91af1e',
        600: '#7c9a16',
        700: '#5e7516',
        800: '#4b5d17',
        900: '#404f18',
        A100: '#b9f6ca',
        A200: '#69f0ae',
        A400: '#00e676',
        A700: '#00c853'
      },
      orange: {
        50: '#fdf2e1',
        100: '#f9deb3',
        200: '#f6c883',
        300: '#f2b253',
        400: '#f0a232',
        500: '#ed931c',
        600: '#e98819',
        700: '#e37916',
        800: '#dc6b14',
        900: '#d15311',
        A100: '#f9deb3',
        A200: '#f0a232',
        A400: '#ed931c',
        A700: '#dc6b14'
      }
    },
    lightGrey: '#cdd9dd',
    palette: {
      primary: {
        main: '#3C5D69',
        light: '#A7C3CD',
        dark: '#304B54',
        contrastText: '#ffffff'
      },
      secondary: {
        main: grey[500],
        light: grey[300],
        dark: grey[600],
        contrastText: '#ffffff'
      },
      info: {
        main: '#567783',
        dark: '#425b64',
        light: '#6d929f'
      },
      warning: {
        main: '#ffbb00',
        dark: '#b28200',
        light: '#ffc833'
      },
      error: {
        main: '#c62828',
        dark: '#991B1B',
        light: '#ff7875'
      }
    },
    typography: {
      fontFamily: 'DIN Next LT Pro',
      h1: {
        fontFamily: 'DIN Next LT Pro',
        fontSize: '2.5rem',
        fontWeight: 'bold'
      },
      h2: {
        fontFamily: 'DIN Next LT Pro',
        fontSize: '2rem'
      },
      h3: {
        fontFamily: 'DIN Next LT Pro',
        fontSize: '1.75rem',
        fontWeight: 'bold'
      },
      h4: {
        fontFamily: 'DIN Next LT Pro',
        fontSize: '1.65rem'
      },
      h5: {
        fontFamily: 'DIN Next LT Pro',
        fontSize: '1.5rem'
      },
      h6: {
        fontFamily: 'DIN Next LT Pro',
        fontSize: '1.25rem'
      }
    }
  });
  const router = Router({ providers: <AppProvider /> });

  return (
    <>
      <AppInsightsContext.Provider value={reactPlugin}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            {localStorage.getItem('maintenanceMode') === 'true' ? <MaintenanceContainer /> : <RouterProvider router={router} />}
            <ToastContainer
              position="bottom-center"
              theme="colored"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
            <ReactQueryDevtools initialIsOpen={false} />
          </ThemeProvider>
        </QueryClientProvider>
      </AppInsightsContext.Provider>
    </>
  );
}

export default App;
